import { Box, CircularProgress, Container, Grid, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import { GET_JOB_OFFER } from '@gql/jobs';
import { GET_PRO } from '@gql/pros';
import { useParams } from 'react-router-dom';
import { useSaveJobOffer } from '../hooks/useSaveJobOffer';
import { usePublishJobOffer } from '../hooks/usePublishJobOffer';
import JobOfferAbout from './components/JobOfferAbout';
import JobOfferPlanInfo from './components/JobOfferPlanInfo';
import JobOfferBenefits from './components/JobOfferBenefits';
import JobOfferCompletionDetail from './components/JobOfferCompletionDetail';
import JobOfferMissions from './components/JobOfferMissions';
import JobOfferTitle from './components/JobOfferProfile';
import JobOfferRecruitmentProcess from './components/JobOfferRecruitmentProcess';
import JobOfferWantedProfile from './components/JobOfferWantedProfile';
import JobOfferWhyChooseUs from './components/JobOfferWhyChooseUs';
import PhotoGallery from './components/PhotoGallery';
import VideoGallery from './components/VideoGallery';
import { FormProvider } from 'react-hook-form';
import JobOfferFloatingBar from '../components/JobOfferFloatingBar';

interface JobOfferDetailProps {
    initialJobOffer?: any;
    onSaveSuccess?: (savedJobOffer: any) => void;
}

const JobOfferDetail: React.FC<JobOfferDetailProps> = ({ initialJobOffer, onSaveSuccess }) => {
    const { slug } = useParams<{ slug: string }>();

    const { data: jobOfferData, loading: fetchingJobOffer, error: jobOfferError } = useQuery(GET_JOB_OFFER, {
        variables: { jobNumber: slug, showInactive: true },
        skip: !slug || !!initialJobOffer,
    });

    const jobOffer = initialJobOffer ?? jobOfferData?.getJobOffer;

    const { data: proData, loading: fetchingPro, error: proError } = useQuery(GET_PRO, {
        variables: {
            proId: jobOffer?.pro?._id,
        },
        skip: !jobOffer?.pro?._id,
    });

    const pro = proData?.getPro;

    const [isWantedProfileEditing, setIsWantedProfileEditing] = useState(false);
    const [isBenefitsEditing, setIsBenefitsEditing] = useState(false);
    const isAnyEditing = isWantedProfileEditing || isBenefitsEditing;

    const { submit, loading: saveLoading, methods } = useSaveJobOffer({
        proId: jobOffer?.pro?._id,
        jobOffer: jobOffer,
        onSuccess: onSaveSuccess,
    });

    const { publish, loading: publishLoading } = usePublishJobOffer({
        jobOfferId: jobOffer?._id,
        jobNumber: jobOffer?.jobNumber,
        proId: jobOffer?.pro?._id,
        onSuccess: onSaveSuccess,
    });

    const { isDirty } = methods.formState;

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (isDirty) {
                e.preventDefault();
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [isDirty]);

    if ((fetchingJobOffer || fetchingPro) && !initialJobOffer) {
        return (
            <Container sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
            </Container>
        );
    }

    if (!initialJobOffer && (jobOfferError || proError || (!fetchingJobOffer && !jobOffer))) {
        return (
            <Container sx={{ justifyContent: 'center', alignItems: 'center' }}>
                <Typography color="error">{jobOfferError?.message ?? proError?.message ?? "Offre d'emploi introuvable."}</Typography>
            </Container>
        );
    }

    return (
        <>
            <FormProvider {...methods}>
                <Grid container spacing={2} position="relative" pb={10}>
                    <Grid item xs={12} md={8}>
                        <Box display="flex" flexDirection="column" gap="16px" width="100%">
                            <JobOfferTitle pro={pro} jobOffer={jobOffer} loading={saveLoading}/>
                            <JobOfferMissions pro={pro} jobOffer={jobOffer} loading={saveLoading}/>
                            <Box display="flex" flexDirection={{
                                xs: 'column',
                                md: isAnyEditing ? 'column' : 'row'
                            }} gap="16px" width="100%" alignItems="stretch">
                                <Box
                                    flex={1}
                                    display="flex"
                                    sx={{
                                        '& > *': {
                                            flex: 1,
                                            width: '100%',
                                        },
                                    }}
                                >
                                    <JobOfferWantedProfile pro={pro} jobOffer={jobOffer} loading={saveLoading} onEditStateChange={setIsWantedProfileEditing}/>
                                </Box>
                                <Box
                                    flex={1}
                                    display="flex"
                                    sx={{
                                        '& > *': {
                                            flex: 1,
                                            width: '100%',
                                        },
                                    }}
                                >
                                    <JobOfferBenefits pro={pro} jobOffer={jobOffer} loading={saveLoading} onEditStateChange={setIsBenefitsEditing}/>
                                </Box>
                            </Box>
                            <JobOfferWhyChooseUs pro={pro} jobOffer={jobOffer} loading={saveLoading}/>
                            <JobOfferRecruitmentProcess pro={pro} jobOffer={jobOffer} loading={saveLoading}/>
                        </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Box display="flex" flexDirection="column" gap="16px" width="100%">
                            <JobOfferPlanInfo currentPlan={jobOffer?.currentPlan} expiredDate={jobOffer?.expiredDate}/>
                            <JobOfferCompletionDetail pro={pro} jobOffer={jobOffer}/>
                            <JobOfferAbout pro={pro} jobOffer={jobOffer} loading={saveLoading}/>
                            <PhotoGallery pro={pro} jobOffer={jobOffer}/>
                            <VideoGallery pro={pro} jobOffer={jobOffer}/>
                        </Box>
                    </Grid>
                </Grid>
                <JobOfferFloatingBar
                    loading={saveLoading || publishLoading}
                    status={jobOffer?.status}
                    proId={jobOffer?.pro?._id}
                    isDirty={isDirty}
                    profileCompletionRate={jobOffer?.profileCompletionRate ?? 0}
                    onSave={() => methods.handleSubmit(submit)()}
                    onPublish={publish}
                />
            </FormProvider>
        </>
    );
};

export default JobOfferDetail;
